import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue } from 'recoil';
import { getMapSettings, setMapSettings } from '../../../../Services/UserService';
import { IMapSettingsValue, IUserMapSettings } from '../../types';
import { isWaypointsOpenAtom } from '../../Waypoints/WaypointState';
import { filterStringAtom, isClusterEnabledAtom, vehicleFocusAtom } from '../../OnlineMapState';
import {
  mapInfoWindowAdditionalAtom,
  mapInfoWindowAlarmsAtom,
  mapInfoWindowBluecoinNameAtom,
  mapInfoWindowBluecoinsAtom,
  mapInfoWindowChristmasIconsAtom,
  mapInfoWindowDoorsAtom,
  mapInfoWindowDriverNameAtom,
  mapInfoWindowEventsAtom,
  mapInfoWindowIdentificationAtom,
  mapInfoWindowLicenceNumberAtom,
  mapInfoWindowMapSettingsAtom,
  mapInfoWindowVehicleNameAtom,
} from './InfoWindowState';
import { isBluecoinsOpenAtom } from '../../Bluecoins/BluecoinState';
import useAnalytics from '../../../../hooks/useAnalytics';
import { saveMapSettings } from '../../MapSettingsService';
import Config from '../../../../../config';
import { isNotDemo } from '../../../../Services/DemoService';

const IDENTIFICATION_ID = 'identification';
const LICENCE_NUMBER_ID = 'licenceNumber';
const DRIVER_NAME_ID = 'driverName';
const BLUECOIN_NAME_ID = 'bluecoinName';
const VEHICLE_NAME_ID = 'vehicleName';
const ALARMS_ID = 'alarms';
const EVENT_ID = 'events';
const CLUSTER_ID = 'clusters';
const DOORS_ID = 'doors';
const ADDITIONAL_ID = 'additional';
const BLUECOINS_ID = 'bluecoins';

export default function VehicleChipsWindow() {
  const [mapSettingsValues, setMapSettingsValues] = useRecoilState(mapInfoWindowMapSettingsAtom);
  const [identification, setIdentification] = useRecoilState(mapInfoWindowIdentificationAtom);
  const [licenceNumber, setLicenceNumber] = useRecoilState(mapInfoWindowLicenceNumberAtom);
  const [driverName, setDriverName] = useRecoilState(mapInfoWindowDriverNameAtom);
  const [bluecoinName, setBluecoinName] = useRecoilState(mapInfoWindowBluecoinNameAtom);
  const [vehicleName, setVehicleName] = useRecoilState(mapInfoWindowVehicleNameAtom);
  const [alarms, setAlarms] = useRecoilState(mapInfoWindowAlarmsAtom);
  const [events, setEvents] = useRecoilState(mapInfoWindowEventsAtom);
  const [christamsIcons, setChristmasIcons] = useRecoilState(mapInfoWindowChristmasIconsAtom);
  const [doors, setDoors] = useRecoilState(mapInfoWindowDoorsAtom);
  const [additional, setAdditional] = useRecoilState(mapInfoWindowAdditionalAtom);
  const [bluecoins, setBluecoins] = useRecoilState(mapInfoWindowBluecoinsAtom);
  const { formatMessage: f } = useIntl();
  const isBluecoinsOpen = useRecoilValue<boolean>(isBluecoinsOpenAtom);
  const [isCluster, setIsCluster] = useRecoilState<boolean>(isClusterEnabledAtom);
  const filterString = useRecoilValue<string>(filterStringAtom);
  const isWaypointsOpen = useRecoilValue<boolean>(isWaypointsOpenAtom);
  const vehicleFocus = useRecoilValue<boolean>(vehicleFocusAtom);
  const { trackEvent } = useAnalytics();

  const saveSettings = React.useCallback((mapSettings: IMapSettingsValue) => {
    const saveMapSettingAsync = async () => {
      saveMapSettings(mapSettings);
    };
    if (isNotDemo()) {
      saveMapSettingAsync();
    }
  }, []);

  React.useEffect(() => {
    if (mapSettingsValues.value) {
      setAlarms(mapSettingsValues.value.alarms);
      setEvents(mapSettingsValues.value.events.enabled);
      setDriverName(mapSettingsValues.value.driverName);
      setIdentification(mapSettingsValues.value.identification);
      if (!vehicleFocus) {
        setIsCluster(mapSettingsValues.value.clusters);
      }
      setLicenceNumber(mapSettingsValues.value.licenceNumber);
      setVehicleName(mapSettingsValues.value.vehicleName);
      setDoors(mapSettingsValues.value.events.doors);
      setAdditional(mapSettingsValues.value.events.additional);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapSettingsValues]);

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let tempIdentification = identification === undefined ? true : identification;
    let tempLicenceNumber = licenceNumber === undefined ? true : licenceNumber;
    let tempDriverName = driverName === undefined ? true : driverName;
    let tempVehicleName = vehicleName === undefined ? true : vehicleName;
    let tempBluecoinName = bluecoinName === undefined ? true : bluecoinName;
    let tempAlarms = alarms === undefined ? true : alarms;
    let tempEvents = events === undefined ? true : events;
    let tempDoors = doors === undefined ? true : doors;
    let tempAdditional = additional === undefined ? true : additional;
    let tempBluecoins = bluecoins === undefined ? true : bluecoins;
    let tempClusters = isCluster === undefined ? true : isCluster;
    trackEvent(['map', 'click', `Vehicles settings change: ${event.currentTarget.id}`]);

    switch (event.currentTarget.id) {
      case IDENTIFICATION_ID:
        tempIdentification = !tempIdentification;
        setIdentification(!tempIdentification);
        break;
      case LICENCE_NUMBER_ID:
        tempLicenceNumber = !tempLicenceNumber;
        setLicenceNumber(tempLicenceNumber);
        break;
      case DRIVER_NAME_ID:
        tempDriverName = !tempDriverName;
        setDriverName(tempDriverName);
        break;
      case VEHICLE_NAME_ID:
        tempVehicleName = !tempVehicleName;
        setVehicleName(tempVehicleName);
        break;
      case BLUECOIN_NAME_ID:
        tempBluecoinName = !tempBluecoinName;
        setBluecoinName(tempBluecoinName);
        break;
      case ALARMS_ID:
        tempAlarms = !tempAlarms;
        setAlarms(tempAlarms);
        break;
      case EVENT_ID:
        tempEvents = !tempEvents;
        setEvents(tempEvents);
        break;
      case DOORS_ID:
        tempDoors = !tempDoors;
        setDoors(tempDoors);
        break;
      case ADDITIONAL_ID:
        tempAdditional = !tempAdditional;
        setAdditional(tempAdditional);
        break;
      case BLUECOINS_ID:
        tempBluecoins = !tempBluecoins;
        setBluecoins(tempBluecoins);
        break;
      case CLUSTER_ID:
        tempClusters = !tempClusters;
        setIsCluster(!isCluster);
        break;

      default:
        break;
    }

    const tempMapSettings: IUserMapSettings = {
      ...mapSettingsValues,
      value: {
        identification: tempIdentification,
        licenceNumber: tempLicenceNumber,
        driverName: tempDriverName,
        vehicleName: tempVehicleName,
        bluecoinName: tempBluecoinName,
        alarms: tempAlarms,
        events: {
          enabled: tempEvents,
          doors: tempDoors,
          additional: tempAdditional,
          bluecoins: tempBluecoins,
        },
        clusters: tempClusters,
        mapType: getMapSettings().value.mapType,
      },
    };

    setMapSettingsValues(tempMapSettings);
    setMapSettings(tempMapSettings);
    saveSettings(tempMapSettings.value);
  };

  const getIsClusterChecked = () => {
    return filterString || vehicleFocus ? getMapSettings().value.clusters : isCluster;
  };

  const handleChristmasIcons = (event: React.ChangeEvent<HTMLInputElement>) => {
    // event.preventDefault();
    // localStorage.setItem('christmasIcons', JSON.stringify(event.currentTarget.checked));
    setChristmasIcons(event.currentTarget.checked);
  };

  return (
    <div className="b-map-custom-control-actions">
      {Config.festivities.christmas.enabled && (
        <div className="row no-gutter align-items-center mb-3">
          <div className="col font-weight-bold">
            {f({ id: 'info.action.window.map.christmasIcons' })}
          </div>
          <div className="col-auto e-checkbox-switch e-checkbox-switch--sm">
            <label>
              <input
                id="festive-icons"
                type="checkbox"
                value="1"
                checked={christamsIcons}
                onChange={handleChristmasIcons}
              />
              <span />
            </label>
          </div>
        </div>
      )}
      <div className="row no-gutter align-items-center mb-3">
        <div className="col font-weight-bold">{f({ id: 'info.action.window.map.alarms' })}</div>
        <div className="col-auto e-checkbox-switch e-checkbox-switch--sm">
          <label>
            <input
              id={ALARMS_ID}
              type="checkbox"
              value="1"
              checked={alarms}
              onChange={handleOnChange}
            />
            <span />
          </label>
        </div>
      </div>
      <div className="row no-gutter align-items-center mb-3">
        <div className="col font-weight-bold">{f({ id: 'info.action.window.map.events' })}</div>
        <div
          className="col-auto e-checkbox-switch e-checkbox-switch--sm"
          // title={props.tooltip || ''}
        >
          <label>
            <input
              id={EVENT_ID}
              type="checkbox"
              value="1"
              checked={events}
              onChange={handleOnChange}
            />
            <span />
          </label>
        </div>
      </div>

      <div className="my-1 ml-2">
        <div className="e-checkbox">
          <label htmlFor={DOORS_ID} data-cy="form-input-checkbox-ecv">
            <input
              type="checkbox"
              id={DOORS_ID}
              name={DOORS_ID}
              disabled={!events}
              checked={doors}
              onChange={handleOnChange}
            />
            <span>{f({ id: 'enum.alarms.notificationMessageTags.doors' })}</span>
          </label>
        </div>
      </div>

      <div className="my-1 ml-2">
        <div className="e-checkbox">
          <label htmlFor={ADDITIONAL_ID} data-cy="form-input-checkbox-ecv">
            <input
              type="checkbox"
              id={ADDITIONAL_ID}
              name={ADDITIONAL_ID}
              disabled={!events}
              checked={additional}
              onChange={handleOnChange}
            />
            <span>{f({ id: 'info.action.window.map.additional' })}</span>
          </label>
        </div>
      </div>

      <div className="my-1 ml-2 mb-3">
        <div className="e-checkbox">
          <label htmlFor={BLUECOINS_ID} data-cy="form-input-checkbox-ecv">
            <input
              type="checkbox"
              id={BLUECOINS_ID}
              name={BLUECOINS_ID}
              disabled={!events}
              checked={bluecoins}
              onChange={handleOnChange}
            />
            <span>{f({ id: 'info.action.window.map.bluecoin' })}</span>
          </label>
        </div>
      </div>

      <div className="row no-gutter align-items-center mb-3">
        <div className="col font-weight-bold">
          {f({ id: 'info.action.window.map.identification' })}
        </div>

        <div
          className="col-auto e-checkbox-switch e-checkbox-switch--sm"
          // title={props.tooltip || ''}
        >
          <label>
            <input
              id={IDENTIFICATION_ID}
              type="checkbox"
              checked={identification}
              onChange={handleOnChange}
            />
            <span />
          </label>
        </div>
      </div>

      <div className="my-1 ml-2">
        <div className="e-checkbox">
          <label htmlFor={LICENCE_NUMBER_ID} data-cy="form-input-checkbox-ecv">
            <input
              type="checkbox"
              id={LICENCE_NUMBER_ID}
              name={LICENCE_NUMBER_ID}
              disabled={!identification}
              checked={licenceNumber}
              onChange={handleOnChange}
            />
            <span>{f({ id: 'info.action.window.map.licence.number' })}</span>
          </label>
        </div>
      </div>

      <div className="my-1 ml-2">
        <div className="e-checkbox">
          <label htmlFor={VEHICLE_NAME_ID} data-cy="form-input-checkbox-vehicle-name">
            <input
              type="checkbox"
              id={VEHICLE_NAME_ID}
              name={VEHICLE_NAME_ID}
              disabled={!identification}
              checked={vehicleName}
              onChange={handleOnChange}
            />
            <span>{f({ id: 'info.action.window.map.vehicle.name' })}</span>
          </label>
        </div>
      </div>

      <div className="my-1 ml-2">
        <div className="e-checkbox">
          <label htmlFor={DRIVER_NAME_ID} data-cy="form-input-checkbox-driver">
            <input
              type="checkbox"
              id={DRIVER_NAME_ID}
              name={DRIVER_NAME_ID}
              disabled={!identification}
              checked={driverName}
              onChange={handleOnChange}
            />
            <span>{f({ id: 'info.action.window.map.driver.name' })}</span>
          </label>
        </div>
      </div>

      <div className="my-1 ml-2">
        <div className="e-checkbox">
          <label htmlFor={BLUECOIN_NAME_ID} data-cy="form-input-checkbox-ble-name">
            <input
              type="checkbox"
              id={BLUECOIN_NAME_ID}
              name={BLUECOIN_NAME_ID}
              disabled={!identification}
              checked={bluecoinName}
              onChange={handleOnChange}
            />
            <span>{f({ id: 'info.action.window.map.bluecoin.name' })}</span>
          </label>
        </div>
      </div>

      {!(isWaypointsOpen || isBluecoinsOpen) && (
        <div className="row no-gutter align-items-center mt-3">
          <div className="col font-weight-bold">{f({ id: 'info.action.window.map.cluster' })}</div>
          <div className="col-auto e-checkbox-switch e-checkbox-switch--sm">
            <label>
              <input
                id={CLUSTER_ID}
                type="checkbox"
                value="1"
                checked={getIsClusterChecked()}
                onChange={handleOnChange}
              />
              <span />
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
